/**
 * DAS-1389: Phase 1 - AI Ideas Review Page
 * Parent ticket: DAS-1388
 *
 * Dev-only page for reviewing AI-generated improvement ideas
 * Created: 2025-11-07
 * Updated: 2026-03-31 — v2 profiles, bulk actions, filters, selection
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Tabs,
  Button,
  message,
  Spin,
  Modal,
  Input,
  Tag,
  Space,
  Statistic,
  Row,
  Col,
  Badge,
  Alert,
  Divider,
  Checkbox,
  Select,
  DatePicker
} from 'antd';
import {
  BulbOutlined,
  CheckOutlined,
  CloseOutlined,
  QuestionCircleOutlined,
  RocketOutlined,
  CodeOutlined,
  BgColorsOutlined,
  BellOutlined,
  BuildOutlined,
  PauseCircleOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  FundOutlined,
  DatabaseOutlined,
  LayoutOutlined,
  EyeOutlined,
  LineChartOutlined,
  BarChartOutlined,
  WarningOutlined,
  FileSearchOutlined,
  CompassOutlined,
  InteractionOutlined,
  ExpandOutlined
} from '@ant-design/icons';
import api from '../functions/api';
import getUserId from '../functions/getUserId';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;

// DAS-2220: Profile icons + labels for v2 (investor/data/ux) with backward compat
const PROFILE_CONFIG = {
  investor: { icon: <FundOutlined />, label: 'Investor', color: '#722ed1' },
  data: { icon: <DatabaseOutlined />, label: 'Data Analyst', color: '#13c2c2' },
  ux: { icon: <LayoutOutlined />, label: 'UX Designer', color: '#eb2f96' },
  // Backward compat for old v1 profiles
  financial: { icon: <FundOutlined />, label: 'Financial', color: '#722ed1' },
  statistical: { icon: <BarChartOutlined />, label: 'Statistical', color: '#13c2c2' },
  ui: { icon: <BgColorsOutlined />, label: 'UI', color: '#eb2f96' },
  engineer: { icon: <CodeOutlined />, label: 'Engineer', color: '#2f54eb' },
};

// Aspect icons mapping — v2 aspects + v1 backward compat
const ASPECT_ICONS = {
  portfolio_data: <FundOutlined />,
  alerts: <BellOutlined />,
  analysis: <LineChartOutlined />,
  reporting: <FileSearchOutlined />,
  data_quality: <DatabaseOutlined />,
  correlations: <BarChartOutlined />,
  missing_data: <WarningOutlined />,
  anomalies: <WarningOutlined />,
  navigation: <CompassOutlined />,
  layout: <LayoutOutlined />,
  readability: <EyeOutlined />,
  interaction: <InteractionOutlined />,
  accessibility: <ExpandOutlined />,
  responsiveness: <LayoutOutlined />,
  technical_debt: <CodeOutlined />,
  ui: <BgColorsOutlined />,
  architecture: <BuildOutlined />
};

const ASPECT_COLORS = {
  portfolio_data: '#722ed1',
  alerts: '#87d068',
  analysis: '#1890ff',
  reporting: '#2f54eb',
  data_quality: '#13c2c2',
  correlations: '#faad14',
  missing_data: '#f5222d',
  anomalies: '#fa541c',
  navigation: '#eb2f96',
  layout: '#a0d911',
  readability: '#597ef7',
  interaction: '#9254de',
  accessibility: '#36cfc9',
  responsiveness: '#ff7a45',
  technical_debt: '#f50',
  ui: '#2db7f5',
  architecture: '#108ee9'
};

const EFFORT_COLORS = { low: '#52c41a', medium: '#faad14', high: '#f5222d' };
const VALUE_COLORS = { low: '#d9d9d9', medium: '#1890ff', high: '#52c41a' };

// DAS-2220: Evidence display component
const EvidenceDisplay = ({ evidenceData, profile }) => {
  const [expanded, setExpanded] = useState(false);
  const [imageError, setImageError] = useState(false);

  if (!evidenceData) return null;

  let parsed;
  try {
    parsed = typeof evidenceData === 'string' ? JSON.parse(evidenceData) : evidenceData;
  } catch {
    return null;
  }

  if (parsed.source === 'llm_gap_analysis' || parsed.source === 'llm_data_analysis') {
    return null;
  }

  // UX profile: screenshot evidence
  if (profile === 'ux' && parsed.screenshot) {
    const screenshotFilename = parsed.screenshot.split('/').pop();
    const screenshotUrl = `/api/ai-ideas/screenshot/${screenshotFilename}`;

    return (
      <div style={{ marginTop: 8 }}>
        <Divider style={{ margin: '8px 0' }} />
        <p style={{ fontWeight: 'bold', fontSize: 12, marginBottom: 4 }}>
          📸 Screenshot Evidence
          <span style={{ fontWeight: 'normal', color: '#888', marginLeft: 8 }}>
            {parsed.viewport} — {parsed.page}
          </span>
        </p>
        {!expanded ? (
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setExpanded(true)}
            style={{ padding: 0, fontSize: 12 }}
          >
            Show screenshot
          </Button>
        ) : (
          <div>
            <Button
              type="link"
              size="small"
              onClick={() => setExpanded(false)}
              style={{ padding: 0, fontSize: 12, marginBottom: 4 }}
            >
              Hide screenshot
            </Button>
            {!imageError ? (
              <img
                src={screenshotUrl}
                alt={`UX screenshot: ${parsed.page}`}
                onClick={() => window.open(screenshotUrl, '_blank')}
                onError={() => setImageError(true)}
                style={{
                  display: 'block',
                  maxWidth: '100%',
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                  cursor: 'pointer',
                  marginTop: 4,
                }}
              />
            ) : (
              <div style={{
                border: '1px solid #d9d9d9',
                borderRadius: 4,
                padding: 8,
                backgroundColor: '#fafafa',
                fontSize: 11,
                color: '#999',
              }}>
                Screenshot not available: {screenshotFilename}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Investor/Data profile: structured evidence
  const hasContent = Object.keys(parsed).some(k =>
    !['source', 'missing', 'error', 'note'].includes(k) && parsed[k] !== null
  );

  if (!hasContent) return null;

  const hasSourceContext = parsed.source_endpoint || parsed.source_page;

  return (
    <div style={{ marginTop: 8 }}>
      <Divider style={{ margin: '8px 0' }} />
      {hasSourceContext && (
        <div style={{
          fontSize: 11,
          color: '#666',
          marginBottom: 6,
          padding: '4px 8px',
          backgroundColor: '#f0f5ff',
          borderRadius: 4,
          border: '1px solid #d6e4ff',
        }}>
          <strong>Source:</strong> {parsed.source_page || 'Unknown page'}
          {parsed.source_jsx && <> — <code style={{ fontSize: 10 }}>{parsed.source_jsx}</code></>}
          {parsed.source_endpoint && <> — <code style={{ fontSize: 10 }}>{parsed.source_endpoint}</code></>}
          {parsed.note && (
            <div style={{ color: '#999', fontStyle: 'italic', marginTop: 2 }}>
              {parsed.note}
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
        <p style={{ fontWeight: 'bold', fontSize: 12, margin: 0 }}>
          📋 Evidence
        </p>
        <Button
          type="link"
          size="small"
          onClick={() => setExpanded(!expanded)}
          style={{ fontSize: 11, padding: '0 4px' }}
        >
          {expanded ? 'Collapse' : 'Show details'}
        </Button>
      </div>
      {expanded && (
        <pre style={{
          backgroundColor: '#f5f5f5',
          border: '1px solid #e8e8e8',
          borderRadius: 4,
          padding: 8,
          fontSize: 11,
          maxHeight: 200,
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}>
          {JSON.stringify(parsed, null, 2)}
        </pre>
      )}
    </div>
  );
};

const AIIdeasReview = () => {
  const [loading, setLoading] = useState(false);
  const [allIdeas, setAllIdeas] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [comment, setComment] = useState('');
  const [rejectionCategory, setRejectionCategory] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeTab, setActiveTab] = useState('pending');
  const userId = getUserId();

  // DAS-2530: trigger a generation run from the page
  const [runModalVisible, setRunModalVisible] = useState(false);
  const [runProfiles, setRunProfiles] = useState(['investor', 'data', 'ux']);
  const [runQuestion, setRunQuestion] = useState('');
  const [runModel, setRunModel] = useState('claude-sonnet-4-6');
  // DAS-3114: the model list comes from the server (Claude models live from Anthropic)
  // until /ai-ideas/models answers (or if it fails) the last fixed list is shown
  const [runModelOptions, setRunModelOptions] = useState([
    { label: 'Claude Opus 4.8 — most capable (priciest)', value: 'claude-opus-4-8' },
    { label: 'Claude Sonnet 4.6 — balanced (recommended)', value: 'claude-sonnet-4-6' },
    { label: 'Claude Haiku 4.5 — fastest & cheapest Claude', value: 'claude-haiku-4-5' },
    { label: 'Gemini 2.5 Pro — Google, strong reasoning', value: 'gemini-2.5-pro' },
    { label: 'Gemini 2.5 Flash — Google, fast & cheap', value: 'gemini-2.5-flash' },
    { label: 'DeepSeek V4 Flash — cheapest (UX screenshots use Claude)', value: 'deepseek-v4-flash' },
  ]);
  // DAS-2639: optional — steer the run toward the prime objective (alert follow-up).
  const [injectPrimeObjective, setInjectPrimeObjective] = useState(false);
  const [runLoading, setRunLoading] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  // Filters
  const [filterProfile, setFilterProfile] = useState(null);
  const [filterEffort, setFilterEffort] = useState(null);
  const [filterValue, setFilterValue] = useState(null);
  const [filterDate, setFilterDate] = useState(null);

  // DAS-3114: load the selectable models when the run dialog opens
  useEffect(() => {
    if (!runModalVisible) return undefined;
    let cancelled = false;
    api.get('/ai-ideas/models')
      .then((res) => {
        const models = res.data?.models || [];
        if (cancelled || !models.length) return;
        setRunModelOptions(models);
        setRunModel((cur) => (models.some((m) => m.value === cur) ? cur : (res.data?.default || models[0].value)));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [runModalVisible]);

  useEffect(() => {
    fetchIdeas();
    fetchStats();
    checkJiraStatuses();
    fetchLastRun();
  }, []);

  // Clear selection when switching tabs
  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeTab]);

  const checkJiraStatuses = async () => {
    try {
      await api.post('/ai-ideas/check-jira-status');
      fetchIdeas();
    } catch (error) {
      console.error('Failed to check Jira statuses:', error);
    }
  };

  const fetchIdeas = async () => {
    setLoading(true);
    try {
      const response = await api.get('/ai-ideas');
      setAllIdeas(response.data.ideas || []);
    } catch (error) {
      message.error('Failed to fetch AI ideas: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/ai-ideas/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  // DAS-2530: fetch the most recent run (for the per-run cost display)
  const fetchLastRun = async () => {
    try {
      const response = await api.get('/ai-ideas/run-summary');
      const runs = response.data?.runs || [];
      setLastRun(runs[0] || null);
    } catch (error) {
      // non-fatal — cost display just stays hidden
    }
  };

  // DAS-2530: kick off a generation run for the selected modes + model
  const handleRunGeneration = async () => {
    if (!runProfiles.length) {
      message.warning('Pick at least one mode to run.');
      return;
    }
    setRunLoading(true);
    try {
      const payload = { profiles: runProfiles, model: runModel };
      const question = runQuestion.trim();
      if (question) payload.question = question;
      if (injectPrimeObjective) payload.inject_prime_objective = true;  // DAS-2639
      const response = await api.post('/ai-ideas/generate', payload);
      const ran = (response.data?.profiles || runProfiles).join(', ');
      const aimed = response.data?.question ? ` aimed at: "${response.data.question}"` : '';
      message.success(
        `Idea generation started (${ran})${aimed} on ${response.data?.model || runModel}. ` +
        'New ideas — and the run cost — appear here in a few minutes; refresh to see them.'
      );
      setRunModalVisible(false);
    } catch (error) {
      message.error('Failed to start idea generation: ' + (error.response?.data?.error || error.message));
    } finally {
      setRunLoading(false);
    }
  };

  // Group ideas by status, then apply filters
  const groupedIdeas = useMemo(() => {
    const groups = {
      pending: [], approved: [], done: [],
      disapproved: [], parked: [], questioning: []
    };

    allIdeas.forEach(idea => {
      // Apply filters
      if (filterProfile && idea.profile !== filterProfile) return;
      if (filterEffort && idea.implementation_effort !== filterEffort) return;
      if (filterValue && idea.value_indicator !== filterValue) return;
      if (filterDate) {
        const ideaDate = new Date(idea.generated_date).toDateString();
        const filterDateStr = filterDate.toDate().toDateString();
        if (ideaDate !== filterDateStr) return;
      }

      if (groups[idea.status]) {
        groups[idea.status].push(idea);
      }
    });

    return groups;
  }, [allIdeas, filterProfile, filterEffort, filterValue, filterDate]);

  // Available filter options (derived from data)
  const filterOptions = useMemo(() => {
    const profiles = new Set();
    const dates = new Set();
    allIdeas.forEach(idea => {
      profiles.add(idea.profile);
      dates.add(idea.generated_date);
    });
    return { profiles: [...profiles].sort(), dates: [...dates].sort().reverse() };
  }, [allIdeas]);

  const handleAction = async (type, ideaOverride = null) => {
    const ideaToUse = ideaOverride || selectedIdea;
    if (!ideaToUse) {
      message.error('No idea selected');
      return;
    }

    setActionLoading(true);
    try {
      let response;

      if (type === 'delete') {
        response = await api.delete(`/ai-ideas/${ideaToUse.id}`);
        const ticketMsg = ideaToUse.jira_ticket ? ' and Jira ticket deleted' : '';
        message.success(`Idea deleted${ticketMsg}`);
      } else {
        const endpoint = `/ai-ideas/${ideaToUse.id}/${type}`;
        const payload = { comment, user_id: userId };
        if ((type === 'disapprove' || type === 'disapprove_after_approval') && rejectionCategory) {
          payload.rejection_category = rejectionCategory;
        }
        response = await api.post(endpoint, payload);

        if (type === 'approve') {
          message.success(`Idea approved! ${response.data.jira_ticket ? `Jira ticket: ${response.data.jira_ticket}` : ''}`);
        } else if (type === 'disapprove' || type === 'disapprove_after_approval') {
          message.success('Idea disapproved' + (type === 'disapprove_after_approval' ? ' and Jira ticket deleted' : ''));
        } else if (type === 'question') {
          if (response.data.answer) {
            Modal.info({
              title: '🤖 AI Agent Response',
              content: (
                <div>
                  <p><strong>Your Question:</strong></p>
                  <p style={{ fontStyle: 'italic', marginBottom: 16 }}>{comment}</p>
                  <p><strong>Agent Answer:</strong></p>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{response.data.answer}</p>
                </div>
              ),
              width: 600,
              okText: 'Got it'
            });
          } else {
            message.success('Question submitted');
          }
        } else if (type === 'park') {
          message.success('Idea parked');
        }
      }

      fetchIdeas();
      fetchStats();
      setModalVisible(false);
      setSelectedIdea(null);
      setComment('');
      setRejectionCategory(null);
    } catch (error) {
      message.error(`Action failed: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk actions
  const handleBulkDisapprove = () => {
    if (selectedIds.size === 0) return;
    Modal.confirm({
      title: `Bulk Disapprove ${selectedIds.size} Ideas`,
      content: 'These ideas will be disapproved and used to improve future generation (learning effect). Continue?',
      okText: `Disapprove ${selectedIds.size}`,
      okType: 'danger',
      onOk: async () => {
        try {
          await api.post('/ai-ideas/bulk-disapprove', {
            idea_ids: [...selectedIds]
          });
          message.success(`${selectedIds.size} ideas disapproved`);
          setSelectedIds(new Set());
          fetchIdeas();
          fetchStats();
        } catch (error) {
          message.error(`Bulk disapprove failed: ${error.message}`);
        }
      }
    });
  };

  const handleBulkDelete = () => {
    const currentIdeas = groupedIdeas[activeTab] || [];
    const idsToDelete = currentIdeas.map(i => i.id);
    if (idsToDelete.length === 0) return;
    Modal.confirm({
      title: `Delete ALL ${idsToDelete.length} Ideas in This Tab`,
      content: (
        <div>
          <p>This will <strong>permanently delete</strong> {idsToDelete.length} ideas from the database.</p>
          <p style={{ color: '#f5222d' }}>No learning effect — these won't influence future idea generation.</p>
          <p>This action cannot be undone.</p>
        </div>
      ),
      okText: `Delete All ${idsToDelete.length}`,
      okType: 'danger',
      onOk: async () => {
        try {
          await api.post('/ai-ideas/bulk-delete', {
            idea_ids: idsToDelete
          });
          message.success(`${idsToDelete.length} ideas deleted`);
          setSelectedIds(new Set());
          fetchIdeas();
          fetchStats();
        } catch (error) {
          message.error(`Bulk delete failed: ${error.message}`);
        }
      }
    });
  };

  const toggleSelection = (ideaId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(ideaId)) {
        next.delete(ideaId);
      } else {
        next.add(ideaId);
      }
      return next;
    });
  };

  const toggleSelectAll = (ideas) => {
    const allSelected = ideas.every(i => selectedIds.has(i.id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(ideas.map(i => i.id)));
    }
  };

  const openActionModal = (idea, type) => {
    setSelectedIdea(idea);
    setModalType(type);
    setComment('');
    setModalVisible(true);
  };

  const renderIdeaCard = (idea) => {
    const getCardActions = () => {
      if (idea.status === 'pending') {
        return [
          <Button type="primary" icon={<CheckOutlined />} onClick={() => openActionModal(idea, 'approve')}>Approve</Button>,
          <Button danger icon={<CloseOutlined />} onClick={() => openActionModal(idea, 'disapprove')}>Disapprove</Button>,
          <Button icon={<PauseCircleOutlined />} onClick={() => openActionModal(idea, 'park')}>Park</Button>,
          <Button icon={<QuestionCircleOutlined />} onClick={() => openActionModal(idea, 'question')}>Question</Button>
        ];
      } else if (idea.status === 'approved') {
        return [
          <Button danger icon={<DeleteOutlined />} onClick={() => {
            setSelectedIdea(idea);
            Modal.confirm({
              title: 'Delete Idea',
              content: 'Delete this idea and its Jira ticket? This cannot be undone.',
              okText: 'Delete', okType: 'danger',
              onOk: async () => { await handleAction('delete', idea); }
            });
          }}>Delete</Button>,
          <Button danger icon={<CloseOutlined />} onClick={() => openActionModal(idea, 'disapprove_after_approval')}>Disapprove After All</Button>
        ];
      } else if (idea.status === 'parked' || idea.status === 'questioning') {
        return [
          <Button type="primary" icon={<CheckOutlined />} onClick={() => openActionModal(idea, 'approve')}>Approve</Button>,
          <Button danger icon={<CloseOutlined />} onClick={() => openActionModal(idea, 'disapprove')}>Disapprove</Button>,
          ...(idea.status === 'questioning' ? [<Button icon={<PauseCircleOutlined />} onClick={() => openActionModal(idea, 'park')}>Park</Button>] : []),
          <Button danger icon={<DeleteOutlined />} onClick={() => {
            setSelectedIdea(idea);
            Modal.confirm({
              title: 'Delete Idea', content: 'Delete this idea? This cannot be undone.',
              okText: 'Delete', okType: 'danger',
              onOk: async () => { await handleAction('delete', idea); }
            });
          }}>Delete</Button>
        ];
      }
      return [];
    };

    const latestInteraction = idea.interactions && idea.interactions.length > 0
      ? idea.interactions[0] : null;

    const isSelectable = ['pending', 'questioning', 'parked'].includes(idea.status);

    return (
      <Card
        key={idea.id}
        style={{ marginBottom: 16 }}
        hoverable
        actions={getCardActions()}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          {isSelectable && (
            <Checkbox
              checked={selectedIds.has(idea.id)}
              onChange={() => toggleSelection(idea.id)}
              style={{ marginRight: 12, marginTop: 4 }}
            />
          )}
          <div style={{ flex: 1 }}>
            <Card.Meta
              avatar={ASPECT_ICONS[idea.aspect]}
              title={
                <Space wrap>
                  {idea.idea.length > 80 ? idea.idea.substring(0, 80) + '...' : idea.idea}
                  <Tag color={EFFORT_COLORS[idea.implementation_effort]}>
                    {idea.implementation_effort.toUpperCase()} effort
                  </Tag>
                  <Tag color={VALUE_COLORS[idea.value_indicator]}>
                    {idea.value_indicator.toUpperCase()} value
                  </Tag>
                  <Tag color={ASPECT_COLORS[idea.aspect]}>
                    {idea.aspect.replace('_', ' ').toUpperCase()}
                  </Tag>
                </Space>
              }
              description={
                <>
                  <p>
                    <strong>Profile:</strong>{' '}
                    <Tag
                      icon={PROFILE_CONFIG[idea.profile]?.icon}
                      color={PROFILE_CONFIG[idea.profile]?.color || '#666'}
                      style={{ marginLeft: 4 }}
                    >
                      {PROFILE_CONFIG[idea.profile]?.label || idea.profile}
                    </Tag>
                  </p>
                  <p><strong>Idea:</strong> {idea.idea}</p>
                  <Divider />
                  <p><strong>Reasoning:</strong> {idea.reason}</p>

                  <EvidenceDisplay evidenceData={idea.evidence_data} profile={idea.profile} />

                  {idea.status === 'approved' && idea.jira_ticket && (
                    <>
                      <Divider />
                      <p>
                        <strong>Jira Ticket:</strong>{' '}
                        <a href={`https://monosoftwebsite.atlassian.net/browse/${idea.jira_ticket}`}
                          target="_blank" rel="noopener noreferrer"
                          style={{ fontWeight: 'bold', color: '#1890ff' }}>
                          {idea.jira_ticket} 🔗
                        </a>
                      </p>
                    </>
                  )}

                  {latestInteraction && latestInteraction.comment && (
                    <>
                      <Divider />
                      <Alert
                        message={
                          latestInteraction.action === 'approve' ? '✅ Approval Comment' :
                          latestInteraction.action === 'disapprove' ? '❌ Disapproval Reason' :
                          latestInteraction.action === 'answer' ? '🤖 AI Agent Answer' :
                          latestInteraction.action === 'park' ? '⏸️ Park Comment' :
                          '❓ Question'
                        }
                        description={latestInteraction.comment}
                        type={
                          latestInteraction.action === 'approve' ? 'success' :
                          latestInteraction.action === 'disapprove' ? 'error' :
                          latestInteraction.action === 'answer' ? 'success' :
                          latestInteraction.action === 'park' ? 'warning' : 'info'
                        }
                        style={{ marginTop: 8 }}
                      />
                    </>
                  )}

                  {idea.interactions && idea.interactions.filter(i => i.action === 'question' || i.action === 'answer').length > 1 && (
                    <>
                      <Divider />
                      <div style={{ marginTop: 8 }}>
                        <p style={{ fontWeight: 'bold', marginBottom: 8 }}>💬 Q&A History:</p>
                        {idea.interactions
                          .filter(i => i.action === 'question' || i.action === 'answer')
                          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                          .slice(0, 3)
                          .map((interaction, idx) => (
                            <div key={idx} style={{
                              marginBottom: 8, padding: 8,
                              backgroundColor: interaction.action === 'question' ? '#f0f5ff' : '#f6ffed',
                              borderRadius: 4, fontSize: 12
                            }}>
                              <strong>{interaction.action === 'question' ? '❓ Q:' : '🤖 A:'}</strong> {interaction.comment}
                            </div>
                          ))}
                      </div>
                    </>
                  )}

                  <p style={{ color: '#888', fontSize: '12px', marginTop: 8 }}>
                    Generated on {new Date(idea.generated_date).toLocaleDateString()}
                  </p>
                </>
              }
            />
          </div>
        </div>
      </Card>
    );
  };

  const renderStatusTab = (status, label, icon, emptyMessage) => {
    const statusIdeas = groupedIdeas[status] || [];
    const isSelectable = ['pending', 'questioning', 'parked'].includes(status);
    const allSelected = statusIdeas.length > 0 && statusIdeas.every(i => selectedIds.has(i.id));

    return (
      <TabPane
        tab={
          <Badge count={statusIdeas.length} offset={[10, 0]}>
            <span>{icon} {label}</span>
          </Badge>
        }
        key={status}
      >
        {/* Bulk action bar */}
        {statusIdeas.length > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 16,
            padding: '8px 12px',
            backgroundColor: '#fafafa',
            borderRadius: 4,
            border: '1px solid #f0f0f0',
          }}>
            {isSelectable && (
              <Checkbox
                checked={allSelected}
                indeterminate={selectedIds.size > 0 && !allSelected}
                onChange={() => toggleSelectAll(statusIdeas)}
              >
                Select all ({statusIdeas.length})
              </Checkbox>
            )}

            {selectedIds.size > 0 && isSelectable && (
              <Button
                danger
                size="small"
                icon={<CloseOutlined />}
                onClick={handleBulkDisapprove}
              >
                Disapprove selected ({selectedIds.size})
              </Button>
            )}

            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={handleBulkDelete}
              style={{ marginLeft: 'auto' }}
            >
              Delete ALL in tab ({statusIdeas.length})
            </Button>
          </div>
        )}

        <Spin spinning={loading}>
          {statusIdeas.length === 0 ? (
            <Alert
              message={emptyMessage}
              description={
                status === 'pending'
                  ? "Check back daily for new AI-generated improvement suggestions."
                  : `No ${status} ideas yet.`
              }
              type="info"
              showIcon
            />
          ) : (
            statusIdeas.map(renderIdeaCard)
          )}
        </Spin>
      </TabPane>
    );
  };

  const getModalTitle = () => {
    if (modalType === 'approve') return 'Approve Idea';
    if (modalType === 'disapprove') return 'Disapprove Idea';
    if (modalType === 'disapprove_after_approval') return 'Disapprove After Approval';
    if (modalType === 'question') return 'Ask Question';
    if (modalType === 'park') return 'Park Idea';
    return '';
  };

  const getModalContent = () => {
    if (modalType === 'approve') {
      return (
        <>
          <p>This will create a Jira ticket for this idea.</p>
          <TextArea rows={4} placeholder="Optional: Add approval comments or implementation notes"
            value={comment} onChange={(e) => setComment(e.target.value)} />
        </>
      );
    } else if (modalType === 'disapprove' || modalType === 'disapprove_after_approval') {
      return (
        <>
          {modalType === 'disapprove_after_approval' && (
            <p><strong>Warning:</strong> This will disapprove the idea, delete the Jira ticket, and move it to disapproved status.</p>
          )}
          <p>Select a rejection reason:</p>
          <Select
            placeholder="Select rejection category (required)"
            value={rejectionCategory}
            onChange={setRejectionCategory}
            style={{ width: '100%', marginBottom: 12 }}
            options={[
              { label: 'Already exists — feature/improvement already implemented', value: 'already_exists' },
              { label: 'Too vague — not actionable, lacks specificity', value: 'too_vague' },
              { label: 'Too complex — effort too high for the value', value: 'too_complex' },
              { label: 'Wrong priority — not relevant to current roadmap', value: 'wrong_priority' },
              { label: 'Bad ROI — cost outweighs benefit', value: 'bad_roi' },
              { label: 'Technically infeasible — cannot be done within architecture', value: 'infeasible' },
            ]}
          />
          <TextArea rows={3} placeholder="Optional: add details about why this idea doesn't fit"
            value={comment} onChange={(e) => setComment(e.target.value)} />
        </>
      );
    } else if (modalType === 'question') {
      return (
        <>
          <p>Ask a question about this idea. The appropriate AI agent will respond.</p>
          <TextArea rows={4} placeholder="What would you like to know about this idea?"
            value={comment} onChange={(e) => setComment(e.target.value)} required />
        </>
      );
    } else if (modalType === 'park') {
      return (
        <>
          <p>Park this idea for later consideration.</p>
          <TextArea rows={4} placeholder="Optional: Add a comment about why this is being parked"
            value={comment} onChange={(e) => setComment(e.target.value)} />
        </>
      );
    }
  };

  const hasActiveFilters = filterProfile || filterEffort || filterValue || filterDate;

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: '100vh' }}>
      <Alert
        message="🔒 Development Only"
        description="This page is only available in development environment."
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        closable
      />

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 18, fontWeight: 600 }}>
            <BulbOutlined /> AI Improvement Ideas
          </span>
          <Button type="primary" icon={<RocketOutlined />} onClick={() => setRunModalVisible(true)}>
            Run generation
          </Button>
        </div>

        {lastRun && (
          <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
            Last run: {lastRun.model} · {(lastRun.profiles || []).join(', ')} ·{' '}
            <strong>${Number(lastRun.cost?.total_cost_usd || 0).toFixed(4)}</strong>{' '}
            ({lastRun.cost?.input_tokens || 0} in / {lastRun.cost?.output_tokens || 0} out tokens) ·{' '}
            {lastRun.ideas_stored} ideas
          </div>
        )}

        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card><Statistic title="Total Ideas" value={stats?.total_ideas || 0} prefix={<BulbOutlined />} /></Card>
          </Col>
          <Col span={6}>
            <Card><Statistic title="Pending" value={stats?.by_status?.pending || 0} valueStyle={{ color: '#1890ff' }} /></Card>
          </Col>
          <Col span={6}>
            <Card><Statistic title="Approved" value={stats?.by_status?.approved || 0} valueStyle={{ color: '#52c41a' }} prefix={<CheckOutlined />} /></Card>
          </Col>
          <Col span={6}>
            <Card><Statistic title="With Jira Tickets" value={stats?.with_jira_tickets || 0} prefix={<RocketOutlined />} /></Card>
          </Col>
        </Row>

        {/* Filters */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 16,
          padding: '8px 12px',
          backgroundColor: '#f9f9f9',
          borderRadius: 4,
          border: '1px solid #f0f0f0',
          flexWrap: 'wrap',
        }}>
          <span style={{ fontWeight: 'bold', fontSize: 12, color: '#666' }}>Filters:</span>

          <Select
            placeholder="Profile"
            allowClear
            size="small"
            style={{ width: 140 }}
            value={filterProfile}
            onChange={setFilterProfile}
          >
            {filterOptions.profiles.map(p => (
              <Option key={p} value={p}>
                {PROFILE_CONFIG[p]?.label || p}
              </Option>
            ))}
          </Select>

          <Select
            placeholder="Effort"
            allowClear
            size="small"
            style={{ width: 120 }}
            value={filterEffort}
            onChange={setFilterEffort}
          >
            <Option value="low">Low</Option>
            <Option value="medium">Medium</Option>
            <Option value="high">High</Option>
          </Select>

          <Select
            placeholder="Value"
            allowClear
            size="small"
            style={{ width: 120 }}
            value={filterValue}
            onChange={setFilterValue}
          >
            <Option value="low">Low</Option>
            <Option value="medium">Medium</Option>
            <Option value="high">High</Option>
          </Select>

          <DatePicker
            placeholder="Generation date"
            allowClear
            size="small"
            style={{ width: 160 }}
            value={filterDate}
            onChange={setFilterDate}
          />

          {hasActiveFilters && (
            <Button
              type="link"
              size="small"
              onClick={() => {
                setFilterProfile(null);
                setFilterEffort(null);
                setFilterValue(null);
                setFilterDate(null);
              }}
            >
              Clear filters
            </Button>
          )}
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab} type="card">
          {renderStatusTab('pending', '⏳ Pending Review', <BulbOutlined />, 'No pending ideas')}
          {renderStatusTab('approved', '✅ Approved', <CheckOutlined />, 'No approved ideas yet')}
          {renderStatusTab('done', '✅ Done', <CheckCircleOutlined />, 'No completed ideas yet')}
          {renderStatusTab('disapproved', '❌ Disapproved', <CloseOutlined />, 'No disapproved ideas yet')}
          {renderStatusTab('parked', '⏸️ Parked', <PauseCircleOutlined />, 'No parked ideas yet')}
          {renderStatusTab('questioning', '❓ Questioning', <QuestionCircleOutlined />, 'No questions yet')}
        </Tabs>
      </Card>

      <Modal
        title={getModalTitle()}
        visible={modalVisible}
        onOk={() => handleAction(modalType)}
        onCancel={() => {
          setModalVisible(false);
          setSelectedIdea(null);
          setComment('');
          setRejectionCategory(null);
        }}
        confirmLoading={actionLoading}
        okButtonProps={{
          disabled: (modalType === 'disapprove' || modalType === 'disapprove_after_approval') && !rejectionCategory,
        }}
        okText={
          modalType === 'approve' ? 'Approve & Create Ticket' :
          modalType === 'disapprove' ? 'Disapprove' :
          modalType === 'disapprove_after_approval' ? 'Disapprove & Delete Ticket' :
          modalType === 'park' ? 'Park Idea' :
          'Submit Question'
        }
      >
        {getModalContent()}
      </Modal>

      <Modal
        title="Run idea generation"
        visible={runModalVisible}
        onOk={handleRunGeneration}
        onCancel={() => setRunModalVisible(false)}
        confirmLoading={runLoading}
        okText="Run now"
        okButtonProps={{ disabled: !runProfiles.length }}
      >
        <p>Pick which modes to run. New ideas land in the table below when it finishes (usually a few minutes).</p>
        <Checkbox.Group
          value={runProfiles}
          onChange={setRunProfiles}
          options={[
            { label: 'Investor — portfolio & alerts ideas', value: 'investor' },
            { label: 'Data — research data quality ideas', value: 'data' },
            { label: 'UX — look & feel of the pages', value: 'ux' },
          ]}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        />

        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 500, marginBottom: 6 }}>Model</div>
          <Select
            value={runModel}
            onChange={setRunModel}
            style={{ width: '100%' }}
            data-testid="ai-ideas-model-select-das3114"
            listHeight={400}
            options={runModelOptions}
          />
        </div>

        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 500, marginBottom: 6 }}>Your question (optional)</div>
          <TextArea
            rows={3}
            value={runQuestion}
            onChange={(e) => setRunQuestion(e.target.value)}
            maxLength={500}
            showCount
            placeholder='Aim this run, e.g. "How could my stock alerts give earlier warning before a drawdown?" Leave blank to let it pick its own angle.'
          />
        </div>

        <div style={{ marginTop: 16 }}>
          <Checkbox
            checked={injectPrimeObjective}
            onChange={(e) => setInjectPrimeObjective(e.target.checked)}
          >
            Aim ideas at the prime objective (alert follow-up)
          </Checkbox>
          <div style={{ fontSize: 12, color: '#888', marginLeft: 24 }}>
            DAS-2639 — when on, agents are steered toward raising the % of alerts followed up and tag each idea with a follow-up lever. Off = today's behaviour.
          </div>
        </div>

        <Alert
          style={{ marginTop: 16 }}
          type="info"
          showIcon
          message="Runs as the configured agent account"
          description="The run signs in as the AI agent account set on the server and only reads — it never changes anything. Each run uses AI credits, so run it when you want fresh ideas."
        />
      </Modal>
    </div>
  );
};

export default AIIdeasReview;
